import React from 'react'

interface DropTargetProps extends Omit<React.HTMLProps<HTMLDivElement>, 'onDrop' | 'onDragOver' | 'onDragLeave'> {
  /** Callback function when files are dropped */
  onDrop?: (files: File[]) => void
  /** Callback function to called every time a drop starts to check if the drop should be allowed */
  canDrop?: (files: File[]) => boolean
}

export const DropTarget = React.forwardRef<HTMLDivElement, DropTargetProps>(
  ({ onDrop, canDrop, className = '', children, ...props }, ref) => {
    const [isDragging, setIsDragging] = React.useState(false)

    const handleDragOver = React.useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (!e.dataTransfer.types.includes('Files')) return
      if (!canDrop || !canDrop(Array.from(e.dataTransfer.files))) return
      setIsDragging(true)
    }, [])

    const handleDragLeave = React.useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
    }, [])

    const handleDrop = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        const files = Array.from(e.dataTransfer.files)
        if (files.length === 0) return
        onDrop?.(files)
      },
      [onDrop]
    )

    return (
      <div
        {...props}
        ref={ref}
        className={`relative transition-colors duration-200 ${
          isDragging ? 'outline-dashed outline-blue-500' : ''
        } ${className}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {children}
        {canDrop && isDragging && (
          <div className="pointer-events-none absolute inset-0 z-[999999] flex items-center justify-center rounded-[inherit] bg-blue-50 bg-opacity-50">
            <div className="font-medium text-blue-500">Drop files here</div>
          </div>
        )}
      </div>
    )
  }
)
